// LiveClassesGroupTable.js
import React, { useMemo, useState } from "react";
import { Button, Badge } from "react-bootstrap";
import TanstackTable from "components/elements/advance-table/TanstackTable";
import TrainingProgramLiveClassContainer from "containers/liveclass/TrainingProgramLiveClassContainer";
import CommonDialog from "components/Dialog/CommonDialog";
import ViewLiveClass from "components/live-class/ViewLiveClass";
import { Eye, Calendar, Clock } from "react-bootstrap-icons";
import { useToast } from "context/toast-context/ToastProvider";
import { formatDisplayTime } from "helper/utils";

const LiveClassesGroupTable = ({
    classes,
    existingUsers,
    fetchAllLiveClasses
}) => {
    const { showToast } = useToast();
    const [openViewLiveClassModal, setOpenViewLiveClassModal] = useState(false);
    const [selectedLiveClass, setSelectedLiveClass] = useState({});

    const openViewLiveClassDetails = (row) => {
        setSelectedLiveClass(row.original);
        setOpenViewLiveClassModal(true);
    };

    const handleViewLiveClassModalClose = () => {
        setOpenViewLiveClassModal(false);
    };

    const handleSaveBtnLiveClassModal = () => {
        // Any save logic if needed
    };

    // Format date for better display
    const formatDisplayDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return dateString;
        }
    };

    // Determine class status based on date and time
    const getClassStatus = (date, startTime, endTime) => {
        if (!date || !startTime) return { status: "Unknown", variant: "secondary" };

        try {
            const classDate = new Date(date);
            const now = new Date();
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            classDate.setHours(0, 0, 0, 0);

            if (classDate > today) {
                return { status: "Upcoming", variant: "primary" };
            } else if (classDate < today) {
                return { status: "Completed", variant: "success" };
            } else {
                // Today - check time
                const [startHours, startMinutes] = startTime.split(':');
                const [endHours, endMinutes] = endTime.split(':');
                const startDateTime = new Date();
                startDateTime.setHours(parseInt(startHours), parseInt(startMinutes));
                const endDateTime = new Date();
                endDateTime.setHours(parseInt(endHours), parseInt(endMinutes));

                if (now < startDateTime) {
                    return { status: "Today", variant: "warning" };
                } else if (now >= startDateTime && now <= endDateTime) {
                    return { status: "Live", variant: "danger" };
                } else {
                    return { status: "Completed", variant: "success" };
                }
            }
        } catch (error) {
            return { status: "Unknown", variant: "secondary" };
        }
    };

    const columns = useMemo(
        () => [
            {
                accessorKey: "id",
                header: () => (
                    <span style={{ fontWeight: "bold", color: "#224D99" }}>SN</span>
                ),
                size: 80,
            },
            {
                accessorKey: "title",
                header: () => (
                    <span style={{ fontWeight: "bold", color: "#224D99" }}>Title</span>
                ),
                cell: ({ getValue, row }) => {
                    const status = getClassStatus(row.original.date, row.original.startTime, row.original.endTime);
                    return (
                        <div>
                            <div style={{ fontWeight: 500, marginBottom: "4px" }}>
                                {getValue()}
                            </div>
                            {/* Status WIP */}
                            {/* <Badge
                                bg={status.variant}
                                style={{
                                    fontSize: "10px",
                                    padding: "2px 8px"
                                }}
                            >
                                {status.status}
                            </Badge> */}
                        </div>
                    );
                },
            },
            {
                accessorKey: "date",
                header: () => (
                    <span style={{ fontWeight: "bold", color: "#224D99" }}>
                        <Calendar size={14} className="me-1" />
                        Date
                    </span>
                ),
                cell: ({ getValue }) => (
                    <div style={{ fontSize: "14px" }}>
                        {formatDisplayDate(getValue())}
                    </div>
                ),
            },
            {
                accessorKey: "startTime",
                header: () => (
                    <span style={{ fontWeight: "bold", color: "#224D99" }}>
                        <Clock size={14} className="me-1" />
                        Start Time
                    </span>
                ),
                cell: ({ getValue }) => (
                    <div style={{ fontSize: "14px", color: "#28a745" }}>
                        {formatDisplayTime(getValue())}
                    </div>
                ),
            },
            {
                accessorKey: "endTime",
                header: () => (
                    <span style={{ fontWeight: "bold", color: "#224D99" }}>
                        <Clock size={14} className="me-1" />
                        End Time
                    </span>
                ),
                cell: ({ getValue }) => (
                    <div style={{ fontSize: "14px", color: "#dc3545" }}>
                        {formatDisplayTime(getValue())}
                    </div>
                ),
            },
            {
                accessorKey: "participants",
                header: () => (
                    <span style={{ fontWeight: "bold", color: "#224D99" }}>
                        Participants
                    </span>
                ),
                cell: ({ getValue }) => {
                    const participants = getValue() || [];
                    return (
                        <div style={{ fontSize: "14px" }}>
                            <Badge
                                bg="info"
                                style={{
                                    backgroundColor: "#17a2b8",
                                    fontSize: "12px"
                                }}
                            >
                                {participants.length} Members
                            </Badge>
                        </div>
                    );
                },
            },
            {
                accessorKey: "actions",
                header: () => (
                    <span style={{ fontWeight: "bold", color: "#224D99" }}>Action</span>
                ),
                size: 120,
                cell: ({ row }) => (
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => openViewLiveClassDetails(row)}
                    >
                        <Eye size={14} className="me-1" />
                        View
                    </Button>
                ),
            },
        ],
        []
    );

    return (
        <>
            <div style={{ minHeight: "400px" }}>
                {classes && classes.length > 0 ? (
                    <TanstackTable
                        columns={columns}
                        data={classes}
                        pagination={true}
                        pageSize={10}
                    />
                ) : (
                    <div
                        className="d-flex flex-column align-items-center justify-content-center"
                        style={{
                            minHeight: "300px",
                            backgroundColor: "#f8f9fa",
                            borderRadius: "8px",
                            border: "2px dashed #dee2e6"
                        }}
                    >
                        <div style={{ fontSize: "48px", marginBottom: "16px" }}>🎥</div>
                        <h5 style={{ color: "#6c757d", marginBottom: "8px" }}>No Live Classes</h5>
                        <p style={{ color: "#adb5bd", textAlign: "center", margin: 0 }}>
                            No live classes have been scheduled for this training program yet.
                            <br />
                            Click "Schedule Live Class" to create one.
                        </p>
                    </div>
                )}
            </div>

            {/* View Live Class Details Modal */}
            {/* <CommonDialog
                open={openViewLiveClassModal}
                handleDialogCLose={handleViewLiveClassModalClose}
                handleSaveOperation={handleSaveBtnLiveClassModal}
                content={
                    <ViewLiveClass
                        liveClass={selectedLiveClass}
                        userGroupUsers={existingUsers}
                        fetchAllLiveClasses={fetchAllLiveClasses}
                        handleViewLiveClassModalClose={handleViewLiveClassModalClose}
                    />
                }
                showCancelBtn={false}
                showSaveBtn={false}
                headerText="Manage Live Class"
            /> */}
            <ViewLiveClass
                show={openViewLiveClassModal}
                onHide={handleViewLiveClassModalClose}
                liveClass={selectedLiveClass}
                userGroupUsers={existingUsers}
                showToast={showToast}
                onMeetingUpdated={fetchAllLiveClasses}
            />

        </>
    );
};

export default LiveClassesGroupTable;